'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import type { OnboardingPath } from '@shared/all/domains/onboarding';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useUser from '@shared/utils/hooks/useUser';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, ChevronDown, ChevronLeft, Clock, Code2, Hash, LayoutGrid, Lock, Search, X } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useReducer, useState } from 'react';
import useCountUp from '../../../hooks/useCountUp';
import { animDelay } from '../../../utils/animDelay';
import { markFunnelSeen } from '../../../utils/onboardingStorage';
import { client } from '../../../utils/trpc';
import { Signup } from '../../auth/Signup';
import { trackOnboarding } from '../trackOnboarding';
import BrandMark from './BrandMark';
import { BOT_POST, BOT_REPLY, LESSON_FACTIIIS, LessonPost, SEED_OIL_POSTS, WORKOUT_POST } from './fixtures';
import OnboardingShell from './OnboardingShell';
import PathOnboarding from './PathOnboarding';

// hours/day scaled to years of a waking life, from the user's own input
const HOURS_TO_YEARS = 2.5;
const MAX_HOURS = 12;
// Founders offer moved to the post-signup flow (PostSignupFlow); the funnel ends
// on the closing "you're set" step.
const DONE_STEP = 9;
const TOTAL_STEPS = 10;

// Path onboarding (picker + per-path flows) is hidden for now; the funnel ends
// on a plain "you're set". Flip to true to bring it back.
const SHOW_PATH_ONBOARDING = false;

// Canonical create-form labels for the requirements the lesson references (web
// has no shared helper; each surface inlines its own map).
const reqLabel: Record<string, string> = {
  [FactiiiRequirement.SOURCE_REQUIRED]: 'Add a Source',
  [FactiiiRequirement.MEDIA_REQUIRED]: 'Add Photo or Video',
  [FactiiiRequirement.TIME_REQUIRED]: 'Pick a Time',
  [FactiiiRequirement.INTEGER_REQUIRED]: 'Add a Number'
};
type UseCase = {
  key: string;
  Icon: typeof LayoutGrid;
  title: string;
  body: string;
};
const USE_CASES: UseCase[] = [{
  key: 'social',
  Icon: LayoutGrid,
  title: 'a feed you control',
  body: 'join spaces, factiii’s communities, and follow factiiis, the tags that link every post about a topic, place, or source. your feed is built from what you choose, not an algorithm.'
}, {
  key: 'research',
  Icon: Search,
  title: 'answers you can trust',
  body: 'ask anything and factiii researches it with ai, then posts the answer with its sources. the next person to search it finds your answer waiting.'
}, {
  key: 'development',
  Icon: Code2,
  title: 'build software while you scroll',
  body: 'run your dev work on boards, where ai writes and ships code on your own hardware, all right beside your feed with no switching apps.'
}, {
  key: 'vault',
  Icon: Lock,
  title: 'a private vault for your data',
  body: 'store your 2fa login codes, and write private posts only you can see. everything’s encrypted on your device, never uploaded, never sold.'
}];

// Resume: an interrupted funnel comes back at its furthest step. The step is
// read from the server (onboarding.resumeStep, derived from TOUR_STEP events),
// not local storage, so it works across devices.
const isResumable = (step: number | null | undefined): step is number => typeof step === 'number' && step >= 1 && step < TOTAL_STEPS;

// Grouped funnel interaction state (CLAUDE.md: one reducer over many useStates).
type FunnelState = {
  step: number;
  hours: number;
  sourceOn: boolean; // step 6's interactive requirement toggle
  signingUp: boolean; // pre-auth: the inline signup form has taken over the canvas
};
const INITIAL_FUNNEL: FunnelState = {
  step: 0,
  hours: 3,
  sourceOn: false,
  signingUp: false
};
type FunnelAction = {
  type: 'hydrate';
  step: number;
} | {
  type: 'next';
} | {
  type: 'hours';
  value: number;
} | {
  type: 'toggleSource';
} | {
  type: 'signup';
} | {
  type: 'backToSteps';
};
const funnelReducer = (state: FunnelState, action: FunnelAction): FunnelState => {
  switch (action.type) {
    case 'hydrate':
      return {
        ...state,
        step: action.step
      };
    case 'next':
      return {
        ...state,
        step: Math.min(state.step + 1, TOTAL_STEPS - 1)
      };
    case 'hours':
      return {
        ...state,
        hours: action.value
      };
    case 'toggleSource':
      return {
        ...state,
        sourceOn: !state.sourceOn
      };
    case 'signup':
      return {
        ...state,
        signingUp: true
      };
    // Leaves the inline signup form. `step` was never touched by 'signup', so
    // this lands on the step the person skipped or finished from.
    case 'backToSteps':
      return {
        ...state,
        signingUp: false
      };
    default:
      return state;
  }
};

// Step-to-step entrance, formerly the `enterAnim` prop spread.
const ENTER_ANIM = 'anim-enter [--enter-duration:400ms] [--enter-ease:cubic-bezier(0,0,0.2,1)] [--enter-x:24px]';

// The shock number: solid indigo, glowing, counting up from 0.
const ShockNumber = t0 => {
  const $ = _c(6);
  const {
    to
  } = t0;
  const value = useCountUp(to);
  let t1;
  if ($[0] !== to || $[1] !== value) {
    t1 = value >= to ? (Math.round(to * 10) / 10).toString() : value.toFixed(1);
    $[0] = to;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const display = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      textShadow: "0 0 38px rgba(129,140,248,0.45)"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== display) {
    t3 = <span className="my-2 block font-display text-[82px] tabular-nums leading-none tracking-tight text-primary" style={t2}>{display} years</span>;
    $[4] = display;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// Small brand-tinted toggle for the requirements lesson.
const Toggle = t0 => {
  const $ = _c(7);
  const {
    on,
    onClick
  } = t0;
  const t1 = `relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-hover"}`;
  const t2 = `absolute top-1 size-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-1"}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <span className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== on || $[3] !== onClick || $[4] !== t1 || $[5] !== t3) {
    t4 = <button type="button" role="switch" aria-checked={on} onClick={onClick} className={t1}>{t3}</button>;
    $[2] = on;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};

// Pill CTA with the brand gradient and glow, using the display font.
const GradientButton = t0 => {
  const $ = _c(3);
  const {
    text,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick || $[1] !== text) {
    t1 = <button type="button" onClick={onClick} className="h-14 w-full rounded-[18px] bg-gradient-to-b from-[#818cf8] to-[#6366f1] font-display text-[17px] tracking-tight text-white shadow-[0_8px_14px_rgba(99,102,241,0.3)] transition-opacity active:opacity-90">{text}</button>;
    $[0] = onClick;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const eyebrow = 'mb-3 text-xs font-bold uppercase tracking-[0.14em] text-primary';
const head = 'font-display text-[32px] leading-[1.15] tracking-tight text-textPrimary';
const sub = 'mt-4 text-base leading-relaxed text-textSecondary';
const foot = 'mt-4 text-[13px] leading-relaxed text-textGray';

// In-lesson reveal, formerly the `revealAnim` prop spread.
const REVEAL_ANIM = 'anim-enter [--enter-duration:350ms] [--enter-y:8px]';

// Step 5: tap the factiii to pull up the posts collected under it.
const LessonCollect = () => {
  const $ = _c(16);
  const [open, setOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className={eyebrow}>the building block</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h1 className={head}>everything here is a <span className="text-primary">factiii</span></h1>;
    t2 = <p className={sub}>a factiii is a tag you add to a post. tap one to pull up every post about the same thing, all in one place.</p>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(true);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `inline-flex items-center gap-2 rounded-full border px-4 py-2 transition-colors ${open ? "border-primary bg-primary/[0.16]" : "border-primary/40 bg-primary/[0.08] hover:bg-primary/[0.16]"}`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="font-display text-[15px] text-primary">seed oil</span>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = open ? "3,412 posts" : "tap to open";
  let t7;
  if ($[5] !== t6) {
    t7 = <span className="text-[11.5px] font-semibold text-textSecondary">{t6}</span>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== open || $[8] !== t4 || $[9] !== t7) {
    t8 = <button type="button" onClick={t3} aria-expanded={open} className={t4}>{t5}{t7}</button>;
    $[7] = open;
    $[8] = t4;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== open) {
    t9 = open ? <div className={REVEAL_ANIM}><div className="my-[6px] ml-6 h-3.5 w-0.5 bg-ds-border-strong" /><div className="flex flex-col gap-2.5">{SEED_OIL_POSTS.map(_temp)}</div></div> : <p className={foot}>every post tagged “seed oil” collects here, so you see every take together.</p>;
    $[11] = open;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <>{t0}{t1}{t2}<div className="mt-5">{t8}{t9}</div></>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
const BUILD_REQS = [{
  Icon: Camera,
  req: FactiiiRequirement.MEDIA_REQUIRED,
  gloss: 'a photo of the set'
}, {
  Icon: Clock,
  req: FactiiiRequirement.TIME_REQUIRED,
  gloss: 'when you did it'
}, {
  Icon: Hash,
  req: FactiiiRequirement.INTEGER_REQUIRED,
  gloss: 'a number, like your reps'
}];

// Step 7: add the three requirements; the post they produce appears when all are on.
const LessonBuild = () => {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [added, setAdded] = useState(t0);
  const allAdded = added.length === BUILD_REQS.length;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = req => setAdded(prev => prev.includes(req) ? prev.filter(r => r !== req) : [...prev, req]);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toggle = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className={eyebrow}>an example</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className={head}>combine <span className="text-primary">requirements</span> to build a tool</h1>;
    t4 = <p className={sub}>say you want to track your workouts. add three requirements to the factiii, then see what a post has to include.</p>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== added) {
    t5 = BUILD_REQS.map(t6 => {
      const {
        Icon,
        req: req_0,
        gloss
      } = t6;
      const on = added.includes(req_0);
      return <button key={req_0} type="button" onClick={() => toggle(req_0)} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${on ? "border-primary bg-primary/[0.12]" : "border-ds-border-standard bg-background-secondary hover:border-primary/50"}`}><Icon size={20} className="text-primary" /><div className="flex-1"><p className="text-[14.5px] font-bold text-textPrimary">{reqLabel[req_0]}</p><p className="text-[12.5px] text-textSecondary">{gloss}</p></div><span className={`flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors ${on ? "border-primary bg-primary" : "border-ds-border-strong"}`}>{on ? <Check size={14} strokeWidth={3} className="text-white" /> : null}</span></button>;
    });
    $[5] = added;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <div className="mt-5 flex flex-col gap-2.5">{t5}</div>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== allAdded) {
    t7 = allAdded ? <div className={REVEAL_ANIM}><div className="my-2 flex justify-center"><ChevronDown size={22} className="text-textGray" /></div><LessonPost post={WORKOUT_POST} /><p className="mt-4 text-sm leading-relaxed text-textSecondary">every post now has to include all three, so nobody can fake it. the same setup tracks prices, meals, or sourced facts.</p></div> : <p className={foot}>add all three to see the post they produce.</p>;
    $[9] = allAdded;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <>{t2}{t3}{t4}{t6}{t7}</>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};

// Step 8: tap to run the bot so its reply appears on demand.
const LessonBot = () => {
  const $ = _c(9);
  const [replied, setReplied] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className={eyebrow}>ai bots</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h1 className={head}>a factiii can be an AI <span className="text-primary">bot</span></h1>;
    t2 = <p className={sub}>give a factiii instructions and a model, and it replies on its own whenever someone tags it on a post.</p>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LessonPost post={BOT_POST} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== replied) {
    t4 = <div className="mt-5">{t3}{replied ? <div className={`${REVEAL_ANIM} ml-5 mt-2 border-l-2 border-ds-border-strong pl-3.5`}><LessonPost post={BOT_REPLY} /></div> : <button type="button" onClick={() => setReplied(true)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-primary/40 bg-primary/[0.08] py-3 text-[14px] font-bold text-primary transition-colors hover:bg-primary/[0.16]">see Einstein reply</button>}</div>;
    $[4] = replied;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className={foot}>people have built bots like Einstein, a translator, and a summarizer. it only runs when the factiii is tagged.</p>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <>{t0}{t1}{t2}{t4}{t5}</>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
const FunnelBody = t0 => {
  const $ = _c(76);
  const {
    onClose
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== router) {
    t1 = target => {
      if (pathname?.startsWith("/runners/")) {
        return;
      }
      router.push(target);
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const leaveTo = t1;
  const {
    currentUser,
    setUser
  } = useUser();
  const queryClient = useQueryClient();
  const {
    addFactiiis
  } = useFactiiis(client);
  const [t2, dispatch] = useReducer(funnelReducer, INITIAL_FUNNEL);
  const {
    step,
    hours,
    sourceOn,
    signingUp
  } = t2;
  const [seeded, setSeeded] = useState(false);
  let t3;
  if ($[3] !== addFactiiis || $[4] !== currentUser.id || $[5] !== currentUser.isAuthenticated || $[6] !== queryClient) {
    t3 = () => {
      queryClient.setQueryData(["awards", currentUser.id], {
        awards: []
      });
      addFactiiis(LESSON_FACTIIIS);
      trackOnboarding("TOUR_STEP", "activation:0");
      if (currentUser.isAuthenticated !== true) {
        setSeeded(true);
        return;
      }
      let ignore = false;
      client.onboarding.resumeStep.query().then(t4 => {
        const [, data] = t4;
        if (ignore) {
          return;
        }
        const resumed = data?.step;
        if (isResumable(resumed)) {
          dispatch({
            type: "hydrate",
            step: resumed
          });
        }
        setSeeded(true);
      });
      return () => {
        ignore = true;
      };
    };
    $[3] = addFactiiis;
    $[4] = currentUser.id;
    $[5] = currentUser.isAuthenticated;
    $[6] = queryClient;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  const name = currentUser.username || "there";
  const years = Math.round(hours * HOURS_TO_YEARS * 10) / 10;
  let t5;
  if ($[9] !== hours) {
    t5 = Math.round(hours * 7);
    $[9] = hours;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const perWeek = t5;
  const progressPct = step >= 2 ? Math.max(8, (step - 1) / (TOTAL_STEPS - 2) * 100) : 0;
  let t6;
  if ($[11] !== step) {
    t6 = () => {
      const nextStep = Math.min(step + 1, TOTAL_STEPS - 1);
      dispatch({
        type: "next"
      });
      trackOnboarding("TOUR_STEP", `activation:${nextStep}`);
    };
    $[11] = step;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const next = t6;
  let t7;
  if ($[13] !== currentUser || $[14] !== setUser) {
    t7 = () => {
      setUser({
        ...currentUser,
        onboardingPath: "completed"
      });
    };
    $[13] = currentUser;
    $[14] = setUser;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const completeOnboarding = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      social: "/home",
      research: "/search",
      development: "/spaces",
      vault: "/home"
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const PATH_TARGET = t8;
  let t9;
  if ($[17] !== currentUser || $[18] !== leaveTo || $[19] !== setUser) {
    t9 = path => {
      trackOnboarding("TOUR_STEP", `activation:path-${path}`);
      trackOnboarding("TOUR_COMPLETED", "activation");
      setUser({
        ...currentUser,
        onboardingPath: path
      });
      leaveTo(PATH_TARGET[path]);
    };
    $[17] = currentUser;
    $[18] = leaveTo;
    $[19] = setUser;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const enterViaPath = t9;
  const preAuth = !!onClose;
  let t10;
  if ($[21] !== completeOnboarding || $[22] !== leaveTo || $[23] !== preAuth) {
    t10 = () => {
      trackOnboarding("TOUR_COMPLETED", "activation");
      markFunnelSeen();
      if (preAuth) {
        dispatch({
          type: "signup"
        });
        return;
      }
      completeOnboarding();
      leaveTo("/home");
    };
    $[21] = completeOnboarding;
    $[22] = leaveTo;
    $[23] = preAuth;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const finish = t10;
  let t11;
  if ($[25] !== completeOnboarding || $[26] !== leaveTo || $[27] !== preAuth || $[28] !== step) {
    t11 = () => {
      trackOnboarding("TOUR_SKIPPED", `activation:${step}`);
      markFunnelSeen();
      if (preAuth) {
        dispatch({
          type: "signup"
        });
        return;
      }
      completeOnboarding();
      leaveTo("/home");
    };
    $[25] = completeOnboarding;
    $[26] = leaveTo;
    $[27] = preAuth;
    $[28] = step;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const skip = t11;
  if (!seeded) {
    let t12;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="min-h-screen bg-background-primary" />;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    return t12;
  }
  let t12;
  if ($[31] !== hours || $[32] !== name || $[33] !== perWeek || $[34] !== sourceOn || $[35] !== step || $[36] !== years) {
    t12 = () => {
      switch (step) {
        case 1:
          {
            return <div key="greet" className={ENTER_ANIM}><p className={eyebrow}>welcome</p><h1 className={head}>good to have you, <span className="text-primary">{name}</span>.</h1><p className={sub}>here’s what factiii can do for you, in about thirty seconds.</p></div>;
          }
        case 2:
          {
            return <div key="hook" className={ENTER_ANIM}><h1 className={head}>how many hours a day do you spend on{" "}<span className="text-primary">social media?</span></h1><div className="mt-9 flex flex-col items-center"><span className="bg-gradient-to-b from-white to-[#818cf8] bg-clip-text font-display text-[88px] tabular-nums leading-none tracking-tight text-transparent">{hours.toFixed(1)}</span><span className="mt-1 text-[15px] font-semibold text-textSecondary">hours a day</span></div><input type="range" min={0} max={MAX_HOURS} step={0.5} value={hours} onChange={e => dispatch({
                type: "hours",
                value: Number(e.target.value)
              })} className="mt-6 w-full accent-primary" /><div className="flex justify-between text-xs font-semibold text-textGray"><span>0</span><span>12+</span></div></div>;
          }
        case 3:
          {
            return <div key="shock" className={ENTER_ANIM}><p className="text-[19px] leading-normal text-textSecondary"><span className="font-bold text-textPrimary">{name}</span>, at that rate you’ll spend</p><ShockNumber to={years} /><p className="text-[19px] leading-normal text-textSecondary">of your life scrolling. that’s{" "}<span className="font-bold text-textPrimary">{perWeek} hours</span>{" "}every week you never get back.</p></div>;
          }
        case 4:
          {
            return <div key="reframe" className={ENTER_ANIM}><h1 className={head}>make that time <span className="text-primary">count</span></h1><p className={sub}>on factiii, that same scrolling gets you somewhere. what pulls you in most?</p><div className="mt-6 flex flex-col gap-3">{USE_CASES.map(_temp2)}</div><p className={foot}>you get all of it either way.</p></div>;
          }
        case 5:
          {
            return <div key="factiii-what" className={ENTER_ANIM}><LessonCollect /></div>;
          }
        case 6:
          {
            return <div key="factiii-req" className={ENTER_ANIM}><p className={eyebrow}>requirements</p><h1 className={head}>a factiii can have{" "}<span className="text-primary">requirements</span></h1><p className={sub}>requirements are things a post must include before it can use the factiii. flip {reqLabel[FactiiiRequirement.SOURCE_REQUIRED]} on and watch what happens to a post with no source.</p><div className={`mt-5 flex items-center justify-between rounded-2xl border bg-background-secondary px-4 py-3 ${sourceOn ? "border-primary" : "border-ds-border-standard"}`}><span className="text-[15px] font-bold text-textPrimary">{reqLabel[FactiiiRequirement.SOURCE_REQUIRED]}</span><Toggle on={sourceOn} onClick={() => dispatch({
                  type: "toggleSource"
                })} /></div><div className={`mt-3 flex items-center gap-3 rounded-2xl border bg-background-secondary px-4 py-3.5 ${sourceOn ? "border-red-500/50" : "border-green-500/50"}`}><div className={`flex size-6 shrink-0 items-center justify-center rounded-full ${sourceOn ? "bg-red-500/[0.16]" : "bg-green-500/[0.16]"}`}>{sourceOn ? <X size={15} strokeWidth={3} className="text-red-500" /> : <Check size={15} strokeWidth={3} className="text-green-500" />}</div><span className="flex-1 text-[15px] font-semibold text-textPrimary">{sourceOn ? "this post has no source, so it can\u2019t post." : "no requirement, so this post goes live."}</span></div><p className={foot}>requirements keep low quality posts out. anything that skips them never gets in.</p></div>;
          }
        case 7:
          {
            return <div key="factiii-combine" className={ENTER_ANIM}><LessonBuild /></div>;
          }
        case 8:
          {
            return <div key="factiii-bot" className={ENTER_ANIM}><LessonBot /></div>;
          }
        case DONE_STEP:
          {
            return <div key="done" className={`${ENTER_ANIM} flex flex-col items-center text-center`}><div className="mb-6 flex size-20 items-center justify-center rounded-full bg-green-500"><Check size={34} strokeWidth={3} className="text-white" /></div><h1 className={`${head} text-center`}>{name}, you’re set.</h1><p className={`${sub} text-center`}>your feed, research, boards, and vault are all one tap away. jump in whenever you’re ready.</p></div>;
          }
        default:
          {
            return null;
          }
      }
    };
    $[31] = hours;
    $[32] = name;
    $[33] = perWeek;
    $[34] = sourceOn;
    $[35] = step;
    $[36] = years;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const renderStep = t12;
  let t13;
  if ($[38] !== finish || $[39] !== next || $[40] !== preAuth || $[41] !== step) {
    t13 = () => {
      if (step === DONE_STEP) {
        return <GradientButton text={preAuth ? "sign up" : "enter factiii"} onClick={finish} />;
      }
      return <GradientButton text="continue" onClick={next} />;
    };
    $[38] = finish;
    $[39] = next;
    $[40] = preAuth;
    $[41] = step;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const renderButtons = t13;
  const t14 = `h-[5px] flex-1 overflow-hidden rounded-full ${step >= 2 ? "bg-hover" : "bg-transparent"}`;
  const t15 = `${progressPct}%`;
  let t16;
  if ($[43] !== t15) {
    t16 = <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{
      width: t15
    }} />;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t14 || $[46] !== t16) {
    t17 = <div className={t14}>{t16}</div>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== skip || $[49] !== step) {
    t18 = step >= 1 && step < DONE_STEP ? <button type="button" onClick={skip} className="py-0.5 text-[13px] font-bold text-textSecondary transition-colors hover:text-textPrimary">skip</button> : null;
    $[48] = skip;
    $[49] = step;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t17 || $[52] !== t18) {
    t19 = <div className="flex items-center gap-3">{t17}{t18}</div>;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const progressHeader = t19;
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => dispatch({
      type: "backToSteps"
    });
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <button type="button" onClick={t20} className="-ml-1 flex items-center gap-1 py-0.5 text-[13px] font-bold text-textSecondary transition-colors hover:text-textPrimary"><ChevronLeft size={16} strokeWidth={2.5} aria-hidden={true} />back</button>;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  const signupHeader = t21;
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div aria-hidden={true} className="pointer-events-none absolute left-1/2 top-[-6%] h-[560px] w-[880px] max-w-[92vw] -translate-x-1/2 rounded-full opacity-35 blur-[130px]" style={{
      background: "radial-gradient(circle, rgba(99,102,241,0.5), transparent 70%)"
    }} />;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== skip) {
    t23 = <button type="button" onClick={skip} className="absolute right-6 top-5 z-20 py-0.5 text-[13px] font-bold text-textSecondary transition-colors hover:text-textPrimary sm:right-10">skip</button>;
    $[57] = skip;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <BrandMark />;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== next) {
    t25 = <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-xl px-6 pb-8 sm:px-10"><GradientButton text="get started" onClick={next} /></div>;
    $[60] = next;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t23 || $[63] !== t25) {
    t26 = <div className="relative flex h-[100dvh] w-full flex-col items-center justify-center overflow-hidden bg-background-primary">{t22}{t23}{t24}{t25}</div>;
    $[62] = t23;
    $[63] = t25;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  const splash = t26;
  if (SHOW_PATH_ONBOARDING && step === DONE_STEP) {
    let t27;
    if ($[65] !== enterViaPath || $[66] !== skip) {
      t27 = <PathOnboarding onEnter={enterViaPath} onSkip={skip} />;
      $[65] = enterViaPath;
      $[66] = skip;
      $[67] = t27;
    } else {
      t27 = $[67];
    }
    return t27;
  }
  let t27;
  if ($[68] !== onClose || $[69] !== progressHeader || $[70] !== renderButtons || $[71] !== renderStep || $[72] !== signingUp || $[73] !== splash || $[74] !== step) {
    t27 = <>{step === 0 && !signingUp ? splash : <OnboardingShell top={signingUp ? signupHeader : progressHeader} footer={signingUp ? null : <div className="flex flex-col gap-1">{renderButtons()}</div>}><>{signingUp ? <div key="signup" className={`${ENTER_ANIM} w-full`}><Signup closeHandler={onClose ?? _temp3} dividerBackground="primary" /></div> : renderStep()}</></OnboardingShell>}</>;
    $[68] = onClose;
    $[69] = progressHeader;
    $[70] = renderButtons;
    $[71] = renderStep;
    $[72] = signingUp;
    $[73] = splash;
    $[74] = step;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
};

// onClose present = pre-auth "Join factiii" mode (finish/skip slide the signup
// form into the funnel; onClose dismisses it on success); absent = post-auth
// mode (completes onboarding on the account).
const ActivationFunnel = t0 => {
  const $ = _c(7);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onClose
  } = t1;
  const [demoClient] = useState(_temp4);
  let t2;
  if ($[2] !== onClose) {
    t2 = <FunnelBody onClose={onClose} />;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== demoClient || $[5] !== t2) {
    t3 = <QueryClientProvider client={demoClient}>{t2}</QueryClientProvider>;
    $[4] = demoClient;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default ActivationFunnel;
function _temp(post) {
  return <LessonPost key={post.id} post={post} />;
}
function _temp2(t0, index) {
  const {
    key,
    Icon,
    title,
    body
  } = t0;
  return <div key={key} style={animDelay(120 + index * 70)} className="anim-enter flex items-start gap-3 rounded-2xl border border-ds-border-standard bg-background-secondary p-4 [--enter-duration:400ms] [--enter-y:12px]"><div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/[0.14]"><Icon size={20} className="text-primary" /></div><div className="flex-1"><p className="text-[15px] font-bold text-textPrimary">{title}</p><p className="mt-0.5 text-[12.5px] leading-snug text-textSecondary">{body}</p></div></div>;
}
function _temp3() {}
function _temp4() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false
      },
      mutations: {
        retry: false
      }
    }
  });
}